/**
 * 計助ダッシュボード用・レガシー用量反応グラフ（散布＋曲線、論文用設定可能）
 */

import {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  createContext,
  useContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Accordion, Button } from 'react-bootstrap';
import KeisukeChart from '../charts/KeisukeChart';
import KeisukeAxis from '../charts/KeisukeAxis';
import KeisukeScatter from '../charts/KeisukeScatter';
import { createLinearScale, calculateNiceTicks } from '../charts/chartUtils';
import type { ChartDimensions, AxisScale, ScatterPoint } from '../charts/types';
import { copySvgElementAsPngToClipboard, normalizeSvgDimensionsForExport } from '../../../../utils/exportUtils';
import { KeisukeDashboardChartExportDropdown } from './KeisukeDashboardChartExportDropdown';
import styles from './KeisukeDashboardChart.module.scss';
import { DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX } from './keisukeDashboardBarChartSettings';
import { safeLocalStorageSetJson } from './keisukeDashboardLocalStorage';
import UTIF from 'utif';

const DEFAULT_MARGIN = { top: 40, right: 40, bottom: 60, left: 120 };

/** レガシー計助 DoseResponseView と同等のグラフ設定型 */
export interface DoseResponseChartSettings {
  size: {
    width: number | null;
    height: number | null;
    plotAreaWidth: number | null;
    plotAreaHeight: number | null;
  };
  xAxis: { label: string; minValue: number | null; maxValue: number | null; labelPosition: number; lineWidth: number };
  yAxis: { label: string; minValue: number | null; maxValue: number | null; labelPosition: number; lineWidth: number };
  xAxisGrid: { show: boolean; lineStyle: 'solid' | 'dashed' | 'dotted'; opacity: number; color: string };
  yAxisGrid: { show: boolean; lineStyle: 'solid' | 'dashed' | 'dotted'; opacity: number; color: string };
  plot: {
    symbolSize: number;
    symbolShape: 'circle' | 'square' | 'triangle' | 'diamond';
    symbolFillColor: string;
    symbolStrokeColor: string;
  };
  curve: { lineWidth: number; lineColor: string; lineStyle: 'solid' | 'dashed' | 'dotted'; opacity: number };
  xScaleLog: boolean;
  textStyles: {
    axisLabel: { fontSize: number; color: string };
    tickLabel: { fontSize: number; color: string };
  };
  /** グラフの背景色。'transparent' または CSS色 */
  chartBackgroundColor: string;
}

const STORAGE_KEY_DR = 'keisuke_dashboard_dose_response_settings';
const STORAGE_KEY_DR_LEGACY = 'keisuke_dashboard_legacy_dose_response_settings';

function createDefaultSettings(
  t: (key: string, opts?: { defaultValue?: string }) => string
): DoseResponseChartSettings {
  return {
    size: { width: null, height: null, plotAreaWidth: null, plotAreaHeight: null },
    xAxis: { label: t('keisuke.doseResponse.dose'), minValue: null, maxValue: null, labelPosition: 40, lineWidth: 3 },
    yAxis: { label: t('keisuke.doseResponse.responseRate'), minValue: null, maxValue: null, labelPosition: -65, lineWidth: 3 },
    xAxisGrid: { show: false, lineStyle: 'dashed', opacity: 0.3, color: '#e5e5e5' },
    yAxisGrid: { show: false, lineStyle: 'dashed', opacity: 0.3, color: '#e5e5e5' },
    plot: { symbolSize: 6, symbolShape: 'circle', symbolFillColor: '#000000', symbolStrokeColor: '#ffffff' },
    curve: { lineWidth: 2, lineColor: '#000000', lineStyle: 'solid', opacity: 0.7 },
    xScaleLog: true,
    textStyles: { axisLabel: { fontSize: 16, color: '#333333' }, tickLabel: { fontSize: 16, color: '#333333' } },
    chartBackgroundColor: 'transparent',
  };
}

function loadDoseResponseSettings(t: (key: string, opts?: { defaultValue?: string }) => string): DoseResponseChartSettings {
  try {
    const saved =
      localStorage.getItem(STORAGE_KEY_DR) ?? localStorage.getItem(STORAGE_KEY_DR_LEGACY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<DoseResponseChartSettings>;
      const def = createDefaultSettings(t);
      return {
        ...def,
        ...parsed,
        size: { ...def.size, ...parsed.size },
        xAxis: { ...def.xAxis, ...parsed.xAxis },
        yAxis: { ...def.yAxis, ...parsed.yAxis },
        xAxisGrid: { ...def.xAxisGrid, ...parsed.xAxisGrid },
        yAxisGrid: { ...def.yAxisGrid, ...parsed.yAxisGrid },
        plot: { ...def.plot, ...parsed.plot },
        curve: { ...def.curve, ...parsed.curve },
        textStyles: {
          ...def.textStyles,
          ...parsed.textStyles,
          axisLabel: { ...def.textStyles.axisLabel, ...parsed.textStyles?.axisLabel },
          tickLabel: { ...def.textStyles.tickLabel, ...parsed.textStyles?.tickLabel },
        },
        chartBackgroundColor: parsed.chartBackgroundColor ?? def.chartBackgroundColor,
      };
    }
  } catch {
    // ignore
  }
  return createDefaultSettings(t);
}

function saveDoseResponseSettings(settings: DoseResponseChartSettings): void {
  safeLocalStorageSetJson(STORAGE_KEY_DR, settings);
}

type DoseLegacyChartSettingsContextValue = {
  chartSettings: DoseResponseChartSettings;
  setChartSettings: Dispatch<SetStateAction<DoseResponseChartSettings>>;
};

const DoseLegacyChartSettingsContext = createContext<DoseLegacyChartSettingsContextValue | null>(null);

export function KeisukeDashboardDoseResponseSettingsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [chartSettings, setChartSettings] = useState(() => loadDoseResponseSettings(t));
  useEffect(() => {
    saveDoseResponseSettings(chartSettings);
  }, [chartSettings]);
  return (
    <DoseLegacyChartSettingsContext.Provider value={{ chartSettings, setChartSettings }}>
      {children}
    </DoseLegacyChartSettingsContext.Provider>
  );
}

function useDoseLegacyChartSettings(): DoseLegacyChartSettingsContextValue {
  const ctx = useContext(DoseLegacyChartSettingsContext);
  if (!ctx) {
    throw new Error('KeisukeDashboardDoseResponseSettingsProvider が必要です');
  }
  return ctx;
}

export type KeisukeDashboardDoseChartLayout = 'split-settings' | 'split-plot';

interface KeisukeDashboardDoseResponseChartProps {
  observed: [number, number][];
  curve?: [number, number][];
  isCalculating?: boolean;
  /** ld50 / ec50 / ec90 / tcid50 — 既定軸ラベルに使用 */
  drType?: string;
  /** 表ヘッダー（用量・濃度列名）→ X軸ラベル */
  tableDoseColumnLabel?: string;
  /** 表ヘッダー（反応列名）→ Y軸ラベル */
  tableEffectColumnLabel?: string;
  layout: KeisukeDashboardDoseChartLayout;
}

export default function KeisukeDashboardDoseResponseChart({
  observed,
  curve = [],
  isCalculating = false,
  tableDoseColumnLabel = '',
  tableEffectColumnLabel = '',
  layout,
}: KeisukeDashboardDoseResponseChartProps) {
  const { t } = useTranslation();
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { chartSettings, setChartSettings } = useDoseLegacyChartSettings();
  const fid = (suffix: string) => `keisuke-dash-dr-${suffix}`;

  useEffect(() => {
    if (!tableDoseColumnLabel && !tableEffectColumnLabel) return;
    setChartSettings((s) => {
      const nextX = tableDoseColumnLabel || s.xAxis.label;
      const nextY = tableEffectColumnLabel || s.yAxis.label;
      if (s.xAxis.label === nextX && s.yAxis.label === nextY) return s;
      return {
        ...s,
        xAxis: { ...s.xAxis, label: nextX },
        yAxis: { ...s.yAxis, label: nextY },
      };
    });
  }, [tableDoseColumnLabel, tableEffectColumnLabel, setChartSettings]);

  const handleResetChartSettings = useCallback(() => {
    const def = createDefaultSettings(t);
    setChartSettings({
      ...def,
      xAxis: { ...def.xAxis, label: tableDoseColumnLabel || def.xAxis.label },
      yAxis: { ...def.yAxis, label: tableEffectColumnLabel || def.yAxis.label },
    });
  }, [t, setChartSettings, tableDoseColumnLabel, tableEffectColumnLabel]);

  const scatterData: ScatterPoint[] = useMemo(
    () => observed.map(([x, y]) => ({ x, y })),
    [observed]
  );

  const xValues = useMemo(() => observed.map(([x]) => x).filter((x) => x > 0), [observed]);
  const xMinAuto = xValues.length > 0 ? Math.min(...xValues) : 1;
  const xMaxAuto = xValues.length > 0 ? Math.max(...xValues) : 10;
  const xMin = chartSettings.xAxis.minValue !== null ? chartSettings.xAxis.minValue : Math.max(0, xMinAuto * 0.5);
  const xMax = chartSettings.xAxis.maxValue !== null ? chartSettings.xAxis.maxValue : xMaxAuto * 1.5;
  const xDomainLinear: [number, number] = [xMin, xMax];
  const xDomainLog: [number, number] = [
    Math.log10(Math.max(1e-10, xMin)),
    Math.log10(Math.max(xMax, 1e-10)),
  ];
  const yMin = chartSettings.yAxis.minValue !== null ? chartSettings.yAxis.minValue : 0;
  const yMax = chartSettings.yAxis.maxValue !== null ? chartSettings.yAxis.maxValue : 100;
  const yDomain: [number, number] = [yMin, yMax];

  const chartMarginForChart = useMemo(() => ({ ...DEFAULT_MARGIN }), []);

  const chartWidth = useMemo(() => {
    if (chartSettings.size.plotAreaWidth != null) {
      return (
        chartSettings.size.plotAreaWidth + chartMarginForChart.left + chartMarginForChart.right
      );
    }
    return chartSettings.size.width ?? undefined;
  }, [chartSettings.size.plotAreaWidth, chartSettings.size.width, chartMarginForChart]);

  /** 右ペイン（split-plot）では親幅に合わせ、保存された固定幅は使わない */
  const chartWidthForKeisuke = layout === 'split-plot' ? undefined : chartWidth;

  const chartHeight = useMemo(() => {
    if (chartSettings.size.plotAreaHeight != null) {
      return (
        chartSettings.size.plotAreaHeight + chartMarginForChart.top + chartMarginForChart.bottom
      );
    }
    return chartSettings.size.height ?? DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX;
  }, [chartSettings.size.plotAreaHeight, chartSettings.size.height, chartMarginForChart]);

  const getExportBaseName = () => `keisuke_dose_response_${new Date().toISOString().slice(0, 10)}`;

  const renderSvgToCanvas = useCallback((callback: (canvas: HTMLCanvasElement) => void) => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const svgString = new XMLSerializer().serializeToString(clone);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      callback(canvas);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, []);

  const handleExportSvg = useCallback(() => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const svgString = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${getExportBaseName()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleCopySvgToClipboard = useCallback(async () => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    await copySvgElementAsPngToClipboard(svg);
  }, []);

  const handleExportPng = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${getExportBaseName()}.png`;
      a.click();
    });
  }, [renderSvgToCanvas]);

  const handleExportJpeg = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/jpeg', 0.92);
      a.download = `${getExportBaseName()}.jpg`;
      a.click();
    });
  }, [renderSvgToCanvas]);

  const handleExportTiff = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const baseName = getExportBaseName();
      try {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const w = canvas.width;
        const h = canvas.height;
        const imageData = ctx.getImageData(0, 0, w, h);
        const tiffBuffer = UTIF.encodeImage(imageData.data, w, h);
        const blob = new Blob([tiffBuffer], { type: 'image/tiff' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}.tiff`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `${baseName}.png`;
        a.click();
      }
    });
  }, [renderSvgToCanvas]);

  if (layout === 'split-plot' && observed.length === 0) {
    return (
      <div className={styles.chartWrap} style={{ opacity: isCalculating ? 0.6 : 1 }}>
        <div className={`${styles.chartRootWrapper} ${styles.chartRoot}`} style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="text-muted small">{t('keisuke.dashboard.chart.noData')}</span>
        </div>
      </div>
    );
  }

  const textStyle = {
    fontSize: chartSettings.textStyles.tickLabel.fontSize,
    fontWeight: 'normal' as const,
    fontStyle: 'normal' as const,
    fontFamily: 'Arial, sans-serif',
    fill: chartSettings.textStyles.tickLabel.color,
  };
  const axisLabelStyle = {
    ...textStyle,
    fontSize: chartSettings.textStyles.axisLabel.fontSize,
    fontWeight: '500' as const,
    fill: chartSettings.textStyles.axisLabel.color,
  };

  return (
    <div
      ref={chartWrapRef}
      className={styles.chartWrap}
      style={{
        opacity: isCalculating ? 0.6 : 1,
        pointerEvents: isCalculating ? 'none' : 'auto',
        flex: '1 1 0',
        minHeight: 0,
        overflowX: layout === 'split-plot' ? 'auto' : 'hidden',
        overflowY: layout === 'split-plot' ? 'auto' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {layout === 'split-settings' ? (
        <div className={styles.chartSettingsSplitRoot}>
      <Accordion className={`mb-2 ${styles.chartSettingsAccordion}`} defaultActiveKey="chart-settings">
        <Accordion.Item eventKey="chart-settings">
          <Accordion.Header className="py-1 small">{t('keisuke.dashboard.chart.settings')}</Accordion.Header>
          <Accordion.Body className="py-2 small">
            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                type="button"
                className={styles.chartSettingsIconBtn}
                onClick={handleResetChartSettings}
                title={t('keisuke.dashboard.chart.resetToDefaultsHint')}
                aria-label={t('keisuke.dashboard.chart.resetToDefaultsHint')}
              >
                <i className="fas fa-undo-alt" aria-hidden />
              </Button>
            </div>
            <div className={`row g-3 ${styles.chartSettingsStack}`}>
              <div className="col-12">
                <div className={styles.chartSettingsColumn}>
                <h6 className="mb-2 small">{t('keisuke.dashboard.chart.graphSettingsSection')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('chart-width')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.width')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('chart-width')} min={200} step={20} placeholder={t('keisuke.dashboard.chart.auto')} value={chartSettings.size.width ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, size: { ...s.size, width: e.target.value ? parseInt(e.target.value, 10) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('chart-height')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.height')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('chart-height')} min={200} step={20} placeholder={t('keisuke.dashboard.chart.auto')} value={chartSettings.size.height ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, size: { ...s.size, height: e.target.value ? parseInt(e.target.value, 10) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('chart-plotAreaWidth')}>
                    <Form.Label className="small">{t('keisuke.anova.plotAreaWidth')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('chart-plotAreaWidth')} min={200} step={20} placeholder={t('keisuke.dashboard.chart.auto')} value={chartSettings.size.plotAreaWidth ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, size: { ...s.size, plotAreaWidth: e.target.value ? parseInt(e.target.value, 10) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('chart-plotAreaHeight')}>
                    <Form.Label className="small">{t('keisuke.anova.plotAreaHeight')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('chart-plotAreaHeight')} min={200} step={20} placeholder={t('keisuke.dashboard.chart.auto')} value={chartSettings.size.plotAreaHeight ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, size: { ...s.size, plotAreaHeight: e.target.value ? parseInt(e.target.value, 10) : null } }))} />
                  </Form.Group>
                  <Form.Group
                    className={styles.chartSizeBackgroundGroup}
                    role="group"
                    aria-label={t('keisuke.dashboard.chart.backgroundColor')}
                  >
                    <div className="small fw-medium mb-1">{t('keisuke.dashboard.chart.backgroundColor')}</div>
                    <Form.Check
                      type="checkbox"
                      className="mb-1"
                      id={fid('chart-bg-transparent')}
                      name={fid('chart-bg-transparent')}
                      label={t('keisuke.dashboard.chart.backgroundColorTransparent')}
                      checked={chartSettings.chartBackgroundColor === 'transparent'}
                      onChange={(e) => setChartSettings((s) => ({ ...s, chartBackgroundColor: e.target.checked ? 'transparent' : '#ffffff' }))}
                    />
                    {chartSettings.chartBackgroundColor !== 'transparent' && (
                      <Form.Control
                        id={fid('chart-bg-color')}
                        type="color"
                        size="sm"
                        name={fid('chart-bg-color')}
                        aria-label={t('keisuke.dashboard.chart.backgroundColor')}
                        value={chartSettings.chartBackgroundColor}
                        onChange={(e) => setChartSettings((s) => ({ ...s, chartBackgroundColor: e.target.value }))}
                        style={{ width: '100%', maxWidth: 120 }}
                      />
                    )}
                  </Form.Group>
                </div>
              </div>
              </div>
              <div className="col-12">
                <div className={styles.chartSettingsColumn}>
                <h6 className="mb-2 small">{t('keisuke.anova.xAxisSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('xAxis-label')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.xAxisLabel')}</Form.Label>
                    <Form.Control type="text" size="sm" name={fid('xAxis-label')} value={chartSettings.xAxis.label} onChange={(e) => setChartSettings((s) => ({ ...s, xAxis: { ...s.xAxis, label: e.target.value } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxis-min')}>
                    <Form.Label className="small">{t('keisuke.anova.yAxisMin')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('xAxis-min')} step={0.1} placeholder={t('keisuke.anova.auto')} value={chartSettings.xAxis.minValue ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, xAxis: { ...s.xAxis, minValue: e.target.value ? parseFloat(e.target.value) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxis-max')}>
                    <Form.Label className="small">{t('keisuke.anova.yAxisMax')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('xAxis-max')} step={0.1} placeholder={t('keisuke.anova.auto')} value={chartSettings.xAxis.maxValue ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, xAxis: { ...s.xAxis, maxValue: e.target.value ? parseFloat(e.target.value) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxis-labelPosition')}>
                    <Form.Label className="small">{t('keisuke.survival.xAxisLabelPosition')}</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name={fid('xAxis-labelPosition')}
                      step={5}
                      value={chartSettings.xAxis.labelPosition}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          xAxis: { ...s.xAxis, labelPosition: parseFloat(e.target.value) || 40 },
                        }))
                      }
                    />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxis-lineWidth')}>
                    <Form.Label className="small">{t('keisuke.survival.axisLineWidth')}（X）</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name={fid('xAxis-lineWidth')}
                      min={0.5}
                      max={10}
                      step={0.5}
                      value={chartSettings.xAxis.lineWidth}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          xAxis: { ...s.xAxis, lineWidth: parseFloat(e.target.value) || 3 },
                        }))
                      }
                    />
                  </Form.Group>
                  <Form.Group controlId={fid('xScaleLog')}>
                    <Form.Check type="checkbox" id={fid('xScaleLog')} name={fid('xScaleLog')} label={t('keisuke.doseResponse.xAxisLog')} checked={chartSettings.xScaleLog} onChange={(e) => setChartSettings((s) => ({ ...s, xScaleLog: e.target.checked }))} />
                  </Form.Group>
                </div>
                <h6 className="mt-2 mb-2 small">{t('keisuke.survival.xAxisGridSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('xAxisGrid-show')}>
                    <Form.Check type="checkbox" id={fid('xAxisGrid-show')} name={fid('xAxisGrid-show')} label={t('keisuke.survival.showGrid')} checked={chartSettings.xAxisGrid.show} onChange={(e) => setChartSettings((s) => ({ ...s, xAxisGrid: { ...s.xAxisGrid, show: e.target.checked } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxisGrid-lineStyle')}>
                    <Form.Label className="small">{t('keisuke.survival.gridLineStyle')}</Form.Label>
                    <Form.Select id={fid('xAxisGrid-lineStyle')} name={fid('xAxisGrid-lineStyle')} size="sm" value={chartSettings.xAxisGrid.lineStyle} onChange={(e) => setChartSettings((s) => ({ ...s, xAxisGrid: { ...s.xAxisGrid, lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' } }))}>
                      <option value="solid">{t('keisuke.dashboard.chart.gridLineSolid')}</option>
                      <option value="dashed">{t('keisuke.dashboard.chart.gridLineDashed')}</option>
                      <option value="dotted">{t('keisuke.dashboard.chart.gridLineDotted')}</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className={styles.chartSettingsFieldRange} controlId={fid('xAxisGrid-opacity')}>
                    <Form.Label className="small" htmlFor={fid('xAxisGrid-opacity-range')}>
                      {t('keisuke.survival.gridOpacity')}
                    </Form.Label>
                    <Form.Range id={fid('xAxisGrid-opacity-range')} name={fid('xAxisGrid-opacity')} min={0} max={1} step={0.1} value={chartSettings.xAxisGrid.opacity} onChange={(e) => setChartSettings((s) => ({ ...s, xAxisGrid: { ...s.xAxisGrid, opacity: parseFloat(e.target.value) } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxisGrid-color')}>
                    <Form.Label className="small">{t('keisuke.survival.gridColor')}</Form.Label>
                    <Form.Control type="color" size="sm" name={fid('xAxisGrid-color')} value={chartSettings.xAxisGrid.color} onChange={(e) => setChartSettings((s) => ({ ...s, xAxisGrid: { ...s.xAxisGrid, color: e.target.value } }))} />
                  </Form.Group>
                </div>
              </div>
              </div>
              <div className="col-12">
                <div className={styles.chartSettingsColumn}>
                <h6 className="mb-2 small">{t('keisuke.anova.yAxisSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('yAxis-label')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.yAxisLabel')}</Form.Label>
                    <Form.Control type="text" size="sm" name={fid('yAxis-label')} value={chartSettings.yAxis.label} onChange={(e) => setChartSettings((s) => ({ ...s, yAxis: { ...s.yAxis, label: e.target.value } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxis-min')}>
                    <Form.Label className="small">{t('keisuke.anova.yAxisMin')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('yAxis-min')} step={0.1} placeholder={t('keisuke.anova.auto')} value={chartSettings.yAxis.minValue ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, yAxis: { ...s.yAxis, minValue: e.target.value ? parseFloat(e.target.value) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxis-max')}>
                    <Form.Label className="small">{t('keisuke.anova.yAxisMax')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('yAxis-max')} step={0.1} placeholder={t('keisuke.anova.auto')} value={chartSettings.yAxis.maxValue ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, yAxis: { ...s.yAxis, maxValue: e.target.value ? parseFloat(e.target.value) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxis-labelPosition')}>
                    <Form.Label className="small">{t('keisuke.survival.yAxisLabelPosition')}</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name={fid('yAxis-labelPosition')}
                      step={5}
                      value={chartSettings.yAxis.labelPosition}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          yAxis: { ...s.yAxis, labelPosition: parseFloat(e.target.value) || -65 },
                        }))
                      }
                    />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxis-lineWidth')}>
                    <Form.Label className="small">{t('keisuke.survival.axisLineWidth')}（Y）</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name={fid('yAxis-lineWidth')}
                      min={0.5}
                      max={10}
                      step={0.5}
                      value={chartSettings.yAxis.lineWidth}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          yAxis: { ...s.yAxis, lineWidth: parseFloat(e.target.value) || 3 },
                        }))
                      }
                    />
                  </Form.Group>
                </div>
                <h6 className="mt-2 mb-2 small">{t('keisuke.survival.yAxisGridSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('yAxisGrid-show')}>
                    <Form.Check type="checkbox" id={fid('yAxisGrid-show')} name={fid('yAxisGrid-show')} label={t('keisuke.survival.showGrid')} checked={chartSettings.yAxisGrid.show} onChange={(e) => setChartSettings((s) => ({ ...s, yAxisGrid: { ...s.yAxisGrid, show: e.target.checked } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxisGrid-lineStyle')}>
                    <Form.Label className="small">{t('keisuke.survival.gridLineStyle')}</Form.Label>
                    <Form.Select id={fid('yAxisGrid-lineStyle')} name={fid('yAxisGrid-lineStyle')} size="sm" value={chartSettings.yAxisGrid.lineStyle} onChange={(e) => setChartSettings((s) => ({ ...s, yAxisGrid: { ...s.yAxisGrid, lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' } }))}>
                      <option value="solid">{t('keisuke.dashboard.chart.gridLineSolid')}</option>
                      <option value="dashed">{t('keisuke.dashboard.chart.gridLineDashed')}</option>
                      <option value="dotted">{t('keisuke.dashboard.chart.gridLineDotted')}</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className={styles.chartSettingsFieldRange} controlId={fid('yAxisGrid-opacity')}>
                    <Form.Label className="small" htmlFor={fid('yAxisGrid-opacity-range')}>
                      {t('keisuke.survival.gridOpacity')}
                    </Form.Label>
                    <Form.Range id={fid('yAxisGrid-opacity-range')} name={fid('yAxisGrid-opacity')} min={0} max={1} step={0.1} value={chartSettings.yAxisGrid.opacity} onChange={(e) => setChartSettings((s) => ({ ...s, yAxisGrid: { ...s.yAxisGrid, opacity: parseFloat(e.target.value) } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxisGrid-color')}>
                    <Form.Label className="small">{t('keisuke.survival.gridColor')}</Form.Label>
                    <Form.Control type="color" size="sm" name={fid('yAxisGrid-color')} value={chartSettings.yAxisGrid.color} onChange={(e) => setChartSettings((s) => ({ ...s, yAxisGrid: { ...s.yAxisGrid, color: e.target.value } }))} />
                  </Form.Group>
                </div>
              </div>
              </div>
              <div className="col-12">
                <div className={styles.chartSettingsColumn}>
                <h6 className="mb-2 small">{t('keisuke.anova.plotSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('plot-symbolSize')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.symbolSize')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('plot-symbolSize')} min={2} max={24} value={chartSettings.plot.symbolSize} onChange={(e) => setChartSettings((s) => ({ ...s, plot: { ...s.plot, symbolSize: parseInt(e.target.value, 10) || 6 } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('plot-symbolShape')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.symbolShape')}</Form.Label>
                    <Form.Select id={fid('plot-symbolShape')} name={fid('plot-symbolShape')} size="sm" value={chartSettings.plot.symbolShape} onChange={(e) => setChartSettings((s) => ({ ...s, plot: { ...s.plot, symbolShape: e.target.value as 'circle' | 'square' | 'triangle' | 'diamond' } }))}>
                      <option value="circle">circle</option>
                      <option value="square">square</option>
                      <option value="triangle">triangle</option>
                      <option value="diamond">diamond</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group controlId={fid('plot-symbolFillColor')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.symbolFillColor')}</Form.Label>
                    <Form.Control type="color" size="sm" name={fid('plot-symbolFillColor')} value={chartSettings.plot.symbolFillColor} onChange={(e) => setChartSettings((s) => ({ ...s, plot: { ...s.plot, symbolFillColor: e.target.value } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('plot-symbolStrokeColor')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.symbolStrokeColor')}</Form.Label>
                    <Form.Control type="color" size="sm" name={fid('plot-symbolStrokeColor')} value={chartSettings.plot.symbolStrokeColor} onChange={(e) => setChartSettings((s) => ({ ...s, plot: { ...s.plot, symbolStrokeColor: e.target.value } }))} />
                  </Form.Group>
                </div>
                <h6 className="mt-2 mb-2 small">{t('keisuke.doseResponse.curveSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('curve-lineWidth')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.curveLineWidth')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('curve-lineWidth')} min={1} max={6} value={chartSettings.curve.lineWidth} onChange={(e) => setChartSettings((s) => ({ ...s, curve: { ...s.curve, lineWidth: parseInt(e.target.value, 10) || 2 } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('curve-lineColor')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.curveColor')}</Form.Label>
                    <Form.Control type="color" size="sm" name={fid('curve-lineColor')} value={chartSettings.curve.lineColor} onChange={(e) => setChartSettings((s) => ({ ...s, curve: { ...s.curve, lineColor: e.target.value } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('curve-lineStyle')}>
                    <Form.Label className="small">{t('keisuke.doseResponse.curveLineStyle')}</Form.Label>
                    <Form.Select id={fid('curve-lineStyle')} name={fid('curve-lineStyle')} size="sm" value={chartSettings.curve.lineStyle} onChange={(e) => setChartSettings((s) => ({ ...s, curve: { ...s.curve, lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' } }))}>
                      <option value="solid">{t('keisuke.dashboard.chart.gridLineSolid')}</option>
                      <option value="dashed">{t('keisuke.dashboard.chart.gridLineDashed')}</option>
                      <option value="dotted">{t('keisuke.dashboard.chart.gridLineDotted')}</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className={styles.chartSettingsFieldRange} controlId={fid('curve-opacity')}>
                    <Form.Label className="small" htmlFor={fid('curve-opacity-range')}>
                      {t('keisuke.doseResponse.curveOpacity')}
                    </Form.Label>
                    <Form.Range id={fid('curve-opacity-range')} name={fid('curve-opacity')} min={0.1} max={1} step={0.1} value={chartSettings.curve.opacity} onChange={(e) => setChartSettings((s) => ({ ...s, curve: { ...s.curve, opacity: parseFloat(e.target.value) } }))} />
                  </Form.Group>
                </div>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('text-axisLabel-fontSize')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.axisLabelFontSize')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('text-axisLabel-fontSize')} min={8} max={24} value={chartSettings.textStyles.axisLabel.fontSize} onChange={(e) => setChartSettings((s) => ({ ...s, textStyles: { ...s.textStyles, axisLabel: { ...s.textStyles.axisLabel, fontSize: parseInt(e.target.value, 10) || 16 } } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('text-axisLabel-color')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.axisLabelColor')}</Form.Label>
                    <Form.Control
                      type="color"
                      size="sm"
                      name={fid('text-axisLabel-color')}
                      value={chartSettings.textStyles.axisLabel.color}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          textStyles: { ...s.textStyles, axisLabel: { ...s.textStyles.axisLabel, color: e.target.value } },
                        }))
                      }
                      style={{ width: '100%', maxWidth: 120 }}
                    />
                  </Form.Group>
                  <Form.Group controlId={fid('text-tickLabel-fontSize')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.tickLabelFontSize')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('text-tickLabel-fontSize')} min={8} max={20} value={chartSettings.textStyles.tickLabel.fontSize} onChange={(e) => setChartSettings((s) => ({ ...s, textStyles: { ...s.textStyles, tickLabel: { ...s.textStyles.tickLabel, fontSize: parseInt(e.target.value, 10) || 16 } } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('text-tickLabel-color')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.tickLabelColor')}</Form.Label>
                    <Form.Control
                      type="color"
                      size="sm"
                      name={fid('text-tickLabel-color')}
                      value={chartSettings.textStyles.tickLabel.color}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          textStyles: { ...s.textStyles, tickLabel: { ...s.textStyles.tickLabel, color: e.target.value } },
                        }))
                      }
                      style={{ width: '100%', maxWidth: 120 }}
                    />
                  </Form.Group>
                </div>
              </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
        </div>
      ) : (
      <div
        style={{
          flex: '1 1 0',
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <div ref={chartContainerRef} className={styles.chartRootWrapper}>
        <KeisukeDashboardChartExportDropdown
          onCopyClipboard={handleCopySvgToClipboard}
          onExportPng={handleExportPng}
          onExportSvg={handleExportSvg}
          onExportTiff={handleExportTiff}
          onExportJpeg={handleExportJpeg}
        />
        <KeisukeChart
          height={chartHeight}
          width={chartWidthForKeisuke}
          margin={chartMarginForChart}
          backgroundColor={chartSettings.chartBackgroundColor || 'transparent'}
          plotAreaBackgroundColor={chartSettings.chartBackgroundColor || 'transparent'}
        >
          {(dimensions: ChartDimensions) => {
            const plotAreaBounds = {
              x: dimensions.margin.left,
              y: dimensions.margin.top,
              width: dimensions.width - dimensions.margin.left - dimensions.margin.right,
              height: dimensions.height - dimensions.margin.top - dimensions.margin.bottom,
            };
            const useLog = chartSettings.xScaleLog && xMinAuto > 0;
            const xDomain = useLog ? xDomainLog : xDomainLinear;
            const toX = (x: number) => (useLog ? Math.log10(Math.max(x, 1e-10)) : x);
            const xScale = createLinearScale(xDomain, [0, plotAreaBounds.width]);
            const xScaleAdjusted = (x: number) => xScale(toX(x));
            const yScaleScatter = createLinearScale(yDomain, [0, plotAreaBounds.height]);
            const yScaleCurve = createLinearScale(yDomain, [plotAreaBounds.height, 0]);
            const xAxisTicks = useLog ? xDomainLog : xDomainLinear;
            const xAxisScale: AxisScale = { domain: xAxisTicks, range: [0, plotAreaBounds.width], ticks: useLog ? calculateNiceTicks(xDomainLog, 5) : calculateNiceTicks(xDomainLinear, 5) };
            const yAxisScale: AxisScale = { domain: yDomain, range: [0, plotAreaBounds.height], ticks: calculateNiceTicks(yDomain, 5) };
            const tickFormatterX = useLog ? (v: number) => { const raw = Math.pow(10, v); return raw < 0.01 || raw > 1000 ? raw.toExponential(1) : String(Math.round(raw * 1000) / 1000); } : undefined;

            const curveDash = chartSettings.curve.lineStyle === 'dashed' ? '8,4' : chartSettings.curve.lineStyle === 'dotted' ? '2,2' : undefined;
            return (
              <>
                <KeisukeAxis bounds={plotAreaBounds} dimensions={dimensions} type="y" scale={yAxisScale} label={chartSettings.yAxis.label} showGrid={chartSettings.yAxisGrid.show} gridLineStyle={chartSettings.yAxisGrid.lineStyle} gridOpacity={chartSettings.yAxisGrid.opacity} gridColor={chartSettings.yAxisGrid.color} axisLineWidth={chartSettings.yAxis.lineWidth} yAxisLabelPosition={chartSettings.yAxis.labelPosition} axisLabelStyle={axisLabelStyle} tickLabelStyle={textStyle} />
                <KeisukeAxis bounds={plotAreaBounds} dimensions={dimensions} type="x" scale={xAxisScale} label={chartSettings.xAxis.label} showGrid={chartSettings.xAxisGrid.show} gridLineStyle={chartSettings.xAxisGrid.lineStyle} gridOpacity={chartSettings.xAxisGrid.opacity} gridColor={chartSettings.xAxisGrid.color} axisLineWidth={chartSettings.xAxis.lineWidth} xAxisLabelPosition={chartSettings.xAxis.labelPosition} axisLabelStyle={axisLabelStyle} tickLabelStyle={textStyle} tickFormatter={tickFormatterX} />
                <KeisukeScatter bounds={plotAreaBounds} data={scatterData} xScale={xScaleAdjusted} yScale={yScaleScatter} pointRadius={chartSettings.plot.symbolSize} symbolShape={chartSettings.plot.symbolShape} symbolFillColor={chartSettings.plot.symbolFillColor} symbolStrokeColor={chartSettings.plot.symbolStrokeColor} />
                {curve.length > 0 && (
                  <g transform={`translate(${plotAreaBounds.x}, ${plotAreaBounds.y})`} opacity={chartSettings.curve.opacity}>
                    <path d={curve.map(([x, y]) => `${xScaleAdjusted(x)},${yScaleCurve(y)}`).reduce((acc, p, i) => (i === 0 ? `M ${p}` : `${acc} L ${p}`), '')} fill="none" stroke={chartSettings.curve.lineColor} strokeWidth={chartSettings.curve.lineWidth} strokeDasharray={curveDash} />
                  </g>
                )}
              </>
            );
          }}
        </KeisukeChart>
      </div>
      </div>
      )}
    </div>
  );
}
